import type { FieldSelection, IdFieldSelection } from '#layers/data-model/types/field-selection';

export function mergeIdFieldSelections(...selections: IdFieldSelection[]): IdFieldSelection {
	return mergeSelections(selections);
}

/** Merge selections, retaining the most permissive relation limit. */
export function mergeFieldSelections(...selections: FieldSelection[]): FieldSelection {
	return mergeSelections(selections);
}

function mergeSelections<T extends string, Sort>(selections: Selection<T, Sort>[]): Selection<T, Sort> {
	if (selections.length === 0) return { fields: [] };
	if (selections.length === 1) return selections[0]!;

	const fields = [...new Set(selections.flatMap((selection) => selection.fields ?? ['*' as const]))];
	const result: Selection<T, Sort> = { fields: fields.includes('*') ? ['*'] : fields };
	const relations = new Map<string, RelationQuery<T, Sort>[]>();

	for (const selection of selections) {
		for (const [name, query] of Object.entries(selection.include ?? {})) {
			const queries = relations.get(name) ?? [];
			queries.push(query);
			relations.set(name, queries);
		}
	}

	if (relations.size > 0) {
		result.include = Object.fromEntries([...relations].map(([name, queries]) => [name, mergeRelationQueries(queries)]));
	}

	return result;
}

function mergeRelationQueries<T extends string, Sort>(queries: RelationQuery<T, Sort>[]): RelationQuery<T, Sort> {
	if (queries.length === 1) return queries[0]!;
	const result: RelationQuery<T, Sort> = mergeSelections(queries);

	if (queries.every((query) => query.limit !== undefined)) {
		result.limit = Math.max(...queries.map((query) => query.limit!));
	}

	const filter = queries.find((query) => query.filter !== undefined)?.filter;
	const sort = queries.find((query) => query.sort?.length)?.sort;
	const offset = queries.find((query) => query.offset !== undefined)?.offset;
	if (filter !== undefined) result.filter = filter;
	if (sort !== undefined) result.sort = sort;
	if (offset !== undefined) result.offset = offset;
	return result;
}

interface Selection<T extends string, Sort> {
	fields?: Array<T | '*'>;
	include?: Record<string, RelationQuery<T, Sort>>;
}

interface RelationQuery<T extends string, Sort> extends Selection<T, Sort> {
	limit?: number;
	offset?: number;
	filter?: object;
	sort?: Sort[];
}
